<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components'
import { EControlBtnUseType } from '@/types/components'

import SVG_DEL_ICON from '@/assets/svg/game-control-gui/del-icon.svg'

defineProps<{
  w: number
  h: number
  left: number
  top: number
  hot: boolean
  parent: boolean
  useType: EControlBtnUseType
}>()
const emits = defineEmits(['dragging', 'dragStop', 'resize', 'delete', 'blur'])
const active = ref(false)
const isHoverDelBtn = ref(false)
</script>

<template>
  <vue-draggable-resizable
    v-if="w"
    v-on-click-outside="() => emits('blur')"
    class="group absolute left-0 top-0"
    :active="active"
    :w="w"
    :h="h"
    :x="left"
    :y="top"
    :resizable="true" :lock-aspect-ratio="true"
    :parent="parent"
    :handles="useType === EControlBtnUseType.ADD ? [] : ['tl', 'tr', 'bl', 'br']"
    @dragging="(...e: number[]) => emits('dragging', e)"
    @drag-stop="(...e: number[]) => emits('dragStop', e)"
    @activated="active = true"
    @deactivated="active = false"
    @resize="(...e: number[]) => emits('resize', e)"
  >
    <!-- resize dot -->
    <template #tl>
      <div class="absolute left-0 top-0 z-2 size-12% flex-center cursor-nwse-resize -translate-x-50% -translate-y-50%">
        <i class="size-6px rd-50% bg-green-5" />
      </div>
    </template>

    <template #tr>
      <div class="absolute right-0 top-0 z-2 size-12% flex-center translate-x-50% cursor-nesw-resize -translate-y-50%">
        <i class="size-6px rd-50% bg-green-5" />
      </div>
    </template>

    <template #bl>
      <div class="absolute bottom-0 left-0 z-2 size-12% flex-center translate-y-50% cursor-nesw-resize -translate-x-50%">
        <i class="size-6px rd-50% bg-green-5" />
      </div>
    </template>

    <template #br>
      <div class="absolute bottom-0 right-0 z-2 size-12% flex-center translate-x-50% translate-y-50% cursor-nwse-resize">
        <i class="size-6px rd-50% bg-green-5" />
      </div>
    </template>

    <!-- content -->
    <div class="relative size-full cursor-pointer b-2px b-#fff b-op-0 b-dashed" :class="{ 'b-op-10': active && useType !== EControlBtnUseType.ADD }">
      <div class="relative size-full" :class="{ 'ctrl-btn-mask': useType !== EControlBtnUseType.ADD, 'show-mask': active || isHoverDelBtn }">
        <div
          class="size-full flex-center b-#fff b-op-10 rd-50% b-solid p-6.6% transition-150 group-active:b-green-5 group-hover:b-op-100"
          :class="{ 'b-green-5 b-op-100': (active && useType !== EControlBtnUseType.ADD) || hot, 'b-red-5! b-op-100!': isHoverDelBtn }"
          :style="{ 'border-width': `${w * 0.033}px` }"
        >
          <div class="size-full flex-center rd-50% bg-#fff bg-op-10" :class="{ 'bg-op-40': hot }">
            <slot />
          </div>
        </div>
      </div>
    </div>

    <!-- del btn -->
    <div v-if="useType !== EControlBtnUseType.ADD" class="absolute size-30% flex-center cursor-pointer b-transparent rd-full bg-#fff bg-op-10 transition-colors-150 -right-3.3% -top-3.3% hover:bg-red-5" :class="active ? 'op-100' : 'op-0 group-hover:op-100'" :style="{ 'border-width': `${w * 0.033}px` }" @click="emits('delete')" @mouseover="isHoverDelBtn = true" @mouseleave="isHoverDelBtn = false">
      <SVG_DEL_ICON class="size-50%" />
    </div>
  </vue-draggable-resizable>
</template>

<style lang="scss" scoped>
.ctrl-btn-mask:hover,
.ctrl-btn-mask.show-mask {
  -webkit-mask-image: url(~/assets/svg/game-control-gui/mask.svg);
  mask-image: url(~/assets/svg/game-control-gui/mask.svg);
  mask-size: 100%;
  mask-position: center;
}
</style>
